import { useCallback, useEffect, useState } from 'react'

/**
 * Desktop sidebar collapse state.
 *
 * The sidebar starts collapsed (icon rail). Expanding it via the toggle at the
 * top persists that choice in localStorage, and the state is mirrored onto
 * `<html data-sidebar="collapsed|expanded">`. Driving it through a root
 * attribute means the content column (`components/layout/main`) can shift its
 * left offset purely in CSS, with no prop drilling through every container.
 *
 * Mobile is unaffected: the drawer behaviour there is driven by
 * `authUser.sidebar` and the desktop-only media queries ignore this attribute.
 */

const STORAGE_KEY = 'sidebarCollapsed'
const ROOT_ATTR = 'data-sidebar'
const EVENT = 'sidebar-collapse-change'

// Collapsed is the default; the rail only stays open once the user has
// explicitly expanded it via the toggle at the top of the sidebar.
const DEFAULT_COLLAPSED = true

export const readSidebarCollapsed = (): boolean => {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY)
        if (stored === '1') return true
        if (stored === '0') return false
        return DEFAULT_COLLAPSED
    } catch {
        return DEFAULT_COLLAPSED
    }
}

const applyRootAttribute = (collapsed: boolean) => {
    if (typeof document === 'undefined') return
    document.documentElement.setAttribute(ROOT_ATTR, collapsed ? 'collapsed' : 'expanded')
}

/** Call once at app start so the very first paint already uses the saved width. */
export const initSidebarCollapsed = () => {
    applyRootAttribute(readSidebarCollapsed())
}

export const setSidebarCollapsed = (collapsed: boolean) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0')
    } catch {
        // Private mode / quota — the in-memory state still works for this tab.
    }
    applyRootAttribute(collapsed)
    window.dispatchEvent(new CustomEvent(EVENT, { detail: collapsed }))
}

/** `[collapsed, setCollapsed]` — shared across every subscriber in the tab. */
export const useSidebarCollapsed = (): [boolean, (next: boolean) => void] => {
    const [collapsed, setLocal] = useState<boolean>(readSidebarCollapsed)

    useEffect(() => {
        const onChange = (event: Event) => setLocal(Boolean((event as CustomEvent<boolean>).detail))
        // Another tab flipped it: follow along so the layout matches on focus.
        const onStorage = (event: StorageEvent) => {
            if (event.key === STORAGE_KEY) {
                const next = event.newValue === '1'
                applyRootAttribute(next)
                setLocal(next)
            }
        }
        window.addEventListener(EVENT, onChange)
        window.addEventListener('storage', onStorage)
        return () => {
            window.removeEventListener(EVENT, onChange)
            window.removeEventListener('storage', onStorage)
        }
    }, [])

    const set = useCallback((next: boolean) => setSidebarCollapsed(next), [])
    return [collapsed, set]
}
